import { getCookieDomainAttribute } from "./publicApp";

export const CURRENT_PROFILE_KEY = "currentProfile";
export const AI_MEMBER_PROFILE_PREFIX = "AI Member:";

const DEFAULT_PROFILE_PLACEHOLDER = "defaultProfile";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

const sanitizeProfileName = (profileName) => {
  if (typeof profileName !== "string") {
    return null;
  }

  const normalizedProfileName = profileName.trim();

  const normalizedProfileNameLower = normalizedProfileName.toLowerCase();

  if (
    !normalizedProfileName ||
    normalizedProfileNameLower === "null" ||
    normalizedProfileNameLower === "undefined" ||
    normalizedProfileNameLower === DEFAULT_PROFILE_PLACEHOLDER.toLowerCase()
  ) {
    return null;
  }

  return normalizedProfileName;
};

const readFromStorage = (storage) => {
  try {
    return sanitizeProfileName(storage?.getItem(CURRENT_PROFILE_KEY));
  } catch (error) {
    return null;
  }
};

const writeToStorage = (storage, profileName) => {
  try {
    if (!storage) {
      return;
    }

    if (profileName) {
      storage.setItem(CURRENT_PROFILE_KEY, profileName);
    } else {
      storage.removeItem(CURRENT_PROFILE_KEY);
    }
  } catch (error) {
    // Ignore storage write failures and fall back to other persistence layers.
  }
};

const getCookieBaseAttributes = () => {
  if (typeof window === "undefined") {
    return "Path=/; SameSite=Lax";
  }

  const secureAttribute = window.location.protocol === "https:" ? "; Secure" : "";
  return `Path=/; SameSite=Lax${secureAttribute}`;
};

const readProfileCookie = () => {
  if (typeof document === "undefined") {
    return null;
  }

  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const cookiePrefix = `${CURRENT_PROFILE_KEY}=`;

  for (const cookie of cookies) {
    if (cookie.startsWith(cookiePrefix)) {
      return sanitizeProfileName(decodeURIComponent(cookie.slice(cookiePrefix.length)));
    }
  }

  return null;
};

const writeProfileCookie = (profileName) => {
  if (typeof document === "undefined") {
    return;
  }

  const baseAttributes = getCookieBaseAttributes();
  const domainAttribute = getCookieDomainAttribute();

  if (profileName) {
    document.cookie = `${CURRENT_PROFILE_KEY}=${encodeURIComponent(profileName)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; ${baseAttributes}${domainAttribute}`;
    return;
  }

  document.cookie = `${CURRENT_PROFILE_KEY}=; Max-Age=0; ${baseAttributes}`;

  if (domainAttribute) {
    document.cookie = `${CURRENT_PROFILE_KEY}=; Max-Age=0; ${baseAttributes}${domainAttribute}`;
  }
};

const persistProfileName = (profileName) => {
  if (typeof window === "undefined") {
    return profileName;
  }

  writeToStorage(window.sessionStorage, profileName);
  writeToStorage(window.localStorage, profileName);
  writeProfileCookie(profileName);

  return profileName;
};

export const getStoredCurrentProfile = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const sessionProfile = readFromStorage(window.sessionStorage);

  if (sessionProfile) {
    return sessionProfile;
  }

  const localProfile = readFromStorage(window.localStorage);

  if (localProfile) {
    return persistProfileName(localProfile);
  }

  const cookieProfile = readProfileCookie();

  if (cookieProfile) {
    return persistProfileName(cookieProfile);
  }

  return null;
};

export const setStoredCurrentProfile = (profileName) => {
  return persistProfileName(sanitizeProfileName(profileName));
};

export const buildAiMemberProfileKey = (aiMember) => {
  const aiMemberId = String(aiMember?._id || aiMember?.id || "").trim();
  return aiMemberId ? `${AI_MEMBER_PROFILE_PREFIX}${aiMemberId}` : null;
};

export const getAiMemberIdFromProfile = (profileName) => {
  const normalizedProfileName = sanitizeProfileName(profileName);
  return normalizedProfileName?.startsWith(AI_MEMBER_PROFILE_PREFIX)
    ? normalizedProfileName.slice(AI_MEMBER_PROFILE_PREFIX.length) || null
    : null;
};

export const isAiMemberProfile = (profileName) => Boolean(getAiMemberIdFromProfile(profileName));

export const isShopProfile = (profileName) => {
  const normalizedProfileName = sanitizeProfileName(profileName);
  return Boolean(
    normalizedProfileName
    && normalizedProfileName !== "User"
    && !isAiMemberProfile(normalizedProfileName)
  );
};